import React, { useEffect, useReducer, useState } from 'react';

/**
 * Shared "Other" free-text handling for dynamic forms (WST-781).
 *
 * The form builder lets an author mark one option of a select/checkbox field as
 * "Other", persisting it as an `x-other-index` marker on the schema property. At
 * render time that marker must be expanded into a real, conditionally-shown
 * free-text field. This module is the single source of truth for that expansion
 * (`expandOtherFields`) and its widget (`OtherTextWidget`), used identically by the
 * builder preview (FormBuilderPage), the published form (WizForm) and the
 * submitted-data viewer (WizFormViewer) so the three render paths cannot diverge.
 */

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * True when an enum option is the literal "other". Enum values are author-supplied and
 * need not be strings — rating scales persist `enum: [1, 2, 3, 4, 5]` — so check the type
 * before comparing. Calling .toLowerCase() on a raw value threw for numeric scales and
 * took the whole form down with it (WST-844).
 */
const isOtherOption = (v: any) => typeof v === 'string' && v.toLowerCase() === 'other';

/**
 * Expand each top-level select/checkbox property that has an "Other" option into a
 * synthetic `<field>__other` free-text property wired to `OtherTextWidget`.
 *
 * The "Other" option is identified by the `x-other-index` marker written by the form
 * builder, falling back to a literal "other" enum value. Inputs are deep-cloned, so
 * the caller's schema/uiSchema are never mutated.
 */
export function expandOtherFields(schema: any, uiSchema: any): { schema: any; uiSchema: any } {
  const s = deepClone(schema);
  const u = deepClone(uiSchema || {});
  if (!s?.properties) return { schema: s, uiSchema: u };

  const injectOther = (fieldKey: string, otherValue: string, widgetType: 'select' | 'checkboxes') => {
    const otherKey = `${fieldKey}__other`;
    s.properties[otherKey] = { type: 'string', title: 'Other', default: '' };
    if (!u['ui:order']) u['ui:order'] = Object.keys(s.properties);
    const idx = u['ui:order'].indexOf(fieldKey);
    if (idx >= 0) u['ui:order'].splice(idx + 1, 0, otherKey);
    else u['ui:order'].push(otherKey);
    u[otherKey] = {
      'ui:widget': 'OtherTextWidget',
      'ui:options': { dependsOn: fieldKey, type: widgetType, otherValue, label: false },
    };
  };

  Object.keys(s.properties).forEach((k) => {
    const prop = s.properties[k];

    // Select: x-other-index marker, falling back to a literal "other" enum value
    const selectOtherIdx = prop?.enum
      ? prop['x-other-index'] ?? prop.enum.findIndex(isOtherOption)
      : -1;
    if (prop?.enum && selectOtherIdx >= 0 && selectOtherIdx < prop.enum.length) {
      injectOther(k, prop.enum[selectOtherIdx], 'select');
    }

    // Checkboxes (array of enum)
    const cbEnum = prop?.type === 'array' ? prop?.items?.enum : null;
    const cbOtherIdx = cbEnum
      ? prop.items['x-other-index'] ?? cbEnum.findIndex(isOtherOption)
      : -1;
    if (cbEnum && cbOtherIdx >= 0 && cbOtherIdx < cbEnum.length) {
      injectOther(k, cbEnum[cbOtherIdx], 'checkboxes');
    }
  });

  return { schema: s, uiSchema: u };
}

/**
 * Free-text input rendered for an "Other" option. Shown only when the field it
 * depends on currently holds (select) or includes (checkboxes) the "Other" value.
 *
 * The dependent value is read from `formContext.formDataRef` — a ref — so this widget
 * never triggers a parent re-render. Because a ref change alone does not re-render this
 * widget when the *dependent* field changes (RJSF only re-renders the field that
 * changed), the host also exposes `formContext.subscribe(cb)`: it calls every subscriber
 * after updating the ref on form change, and this widget force-renders to re-evaluate
 * `show`. Local state + onBlur avoid focus loss on re-mount. The host must keep
 * `formDataRef` current ref-only (never via setState — see WizForm / WST-781).
 */
export function OtherTextWidget({ id, value, onChange, options, formContext }: any) {
  const [localValue, setLocalValue] = useState(value || '');
  const [, forceRender] = useReducer((c) => c + 1, 0);
  const dependsOn = options?.dependsOn;
  const type = options?.type;
  const otherValue = options?.otherValue || 'Other';
  // Re-render on every host form-data change so show/hide tracks the dependent field
  // without the host having to re-render the whole form (which would lose focus).
  useEffect(() => {
    if (!formContext?.subscribe) return;
    return formContext.subscribe(forceRender);
  }, [formContext]);
  const selected = formContext?.formDataRef?.current?.[dependsOn];
  const show = type === 'select' ? selected === otherValue : Array.isArray(selected) && selected?.includes(otherValue);
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);
  if (!show) return <></>;
  return (
    <input
      id={id}
      className="form-control"
      value={localValue}
      placeholder="Please elaborate..."
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => onChange(localValue)}
    />
  );
}
